/**
 * Centralized API layer for Cloud Resources.
 *
 * Uses the shared Axios instance which includes JWT interceptors
 * and automatic token refresh handling.
 *
 * All responses are unwrapped from the backend's @format_api_response
 * decorator format {status, code, message, data} → just the inner data.
 */

import api from '../../../api';

const BASE = '/api/cloud-resources';

/**
 * Unwrap the @format_api_response decorator envelope.
 * Backend returns {status, code, message, data} — we replace response.data
 * with the inner `data` field so callers get clean payloads.
 */
const unwrap = (promise) =>
  promise.then((response) => {
    if (
      response.data &&
      typeof response.data === 'object' &&
      'status' in response.data &&
      'code' in response.data &&
      'data' in response.data
    ) {
      response.data = response.data.data;
    }
    return response;
  });

export const cloudResourcesApi = {
  // ── Resources ──────────────────────────────────────────────────────────

  /** List resources with optional query params (filters, pagination). */
  listResources: (params) => unwrap(api.get(`${BASE}/`, { params })),

  /** Batched live-status refresh — describes up to 100 resources in
   *  parallel on the backend, returning per-resource phase / health.
   *  ``data`` shape: ``{ cloud_account_id, resource_unique_ids: [...] }``.
   *  Use one call per cloud-account group; multiple groups can fan out
   *  in parallel via Promise.all. Throttled at 60/min per user. */
  refreshResourceStatuses: (data) => unwrap(api.post(`${BASE}/refresh/`, data)),

  /** Get a single resource by ID. */
  getResource: (id) => unwrap(api.get(`${BASE}/${id}/`)),

  /** Create a new resource. */
  createResource: (data) => unwrap(api.post(`${BASE}/`, data)),

  /** Update an existing resource. */
  updateResource: (id, data) => unwrap(api.put(`${BASE}/${id}/`, data)),

  /** Delete a resource. */
  /**
   * Delete a cloud resource. Pass ``{ force: true }`` to bypass the
   * phase + deletion-protection gates (admin-only on the backend) so
   * an operator can clean up a row stuck in PROVISIONING / DELETING.
   */
  deleteResource: (id, opts = {}) =>
    unwrap(
      api.delete(
        `${BASE}/${id}/${opts.force ? '?force=true' : ''}`,
      ),
    ),

  /** Remove imported resources from Atmosly (no cloud infrastructure is touched).
   *  Pass ``{ resourceId }`` (DB primary key) to remove exactly one row,
   *  or ``{ cloudAccountId }`` to remove all imported for a specific account. */
  removeAllImported: ({ cloudAccountId, resourceId } = {}) =>
    unwrap(
      api.delete(`${BASE}/imported/remove-all/`, {
        params: {
          ...(resourceId ? { resource_id: resourceId } : {}),
          ...(cloudAccountId && !resourceId ? { cloud_account_id: cloudAccountId } : {}),
        },
      }),
    ),

  // ── Approval ───────────────────────────────────────────────────────────

  /** Drift: on-demand diff (re-describes live vs Atmosly's recorded baseline). */
  getDriftDiff: (id) => unwrap(api.get(`${BASE}/${id}/drift/`)),

  /** Drift: accept the live cloud state as the new baseline (no cloud change).
   *  Optional `fields` accepts only those fields; omit to accept all. */
  acceptDrift: (id, fields) => unwrap(api.post(`${BASE}/${id}/drift/accept/`, fields ? { fields } : {})),

  /** Drift: re-apply Atmosly's recorded config to the cloud (revert the drift).
   *  Optional `fields` reverts only those fields; omit to revert all. */
  reconcileDrift: (id, fields) => unwrap(api.post(`${BASE}/${id}/drift/reconcile/`, fields ? { fields } : {})),

  /** Stop (pause) a running resource — RDS / Aurora / EC2 / DocumentDB. */
  stopResource: (id) => unwrap(api.post(`${BASE}/${id}/stop/`, {})),

  /** Start (resume) a stopped resource. */
  startResource: (id) => unwrap(api.post(`${BASE}/${id}/start/`, {})),

  /** Approve a pending resource request. */
  approveResource: (id, data) => unwrap(api.post(`${BASE}/${id}/approve/`, data)),

  /** Reject a pending resource request. */
  rejectResource: (id, data) => unwrap(api.post(`${BASE}/${id}/reject/`, data)),

  /** Cancel a pending resource request. */
  cancelResource: (id, data) => unwrap(api.post(`${BASE}/${id}/cancel/`, data)),

  // ── Discovery ──────────────────────────────────────────────────────────

  /** Get the field schema for a given resource type. */
  getFieldSchema: (resourceType) =>
    unwrap(api.get(`${BASE}/discover/schema/`, { params: { resource_type: resourceType } })),

  /** Validate cloud account credentials before proceeding. */
  validateCredentials: (cloudAccountId) =>
    unwrap(api.get(`${BASE}/validate-credentials/`, { params: { cloud_account_id: cloudAccountId } })),

  /** Discover existing resources for a given category (e.g. VPC, subnet, security group). */
  discoverResources: (category, params) =>
    unwrap(api.get(`${BASE}/discover/${category}/`, { params })),

  // ── Import ─────────────────────────────────────────────────────────────

  /** Create a new DB subnet group in the cloud account. */
  createDBSubnetGroup: (data) => unwrap(api.post(`${BASE}/db-subnet-groups/create/`, data)),

  /** Create a new ElastiCache cache subnet group in the cloud account. */
  createElastiCacheSubnetGroup: (data) => unwrap(api.post(`${BASE}/elasticache-subnet-groups/create/`, data)),

  /** Import an existing cloud resource into management. */
  importResource: (data) => unwrap(api.post(`${BASE}/import/`, data)),

  // ── Key Pairs ─────────────────────────────────────────────────────────

  /** Create a new EC2 key pair. Returns private key material for download. */
  createKeyPair: (data) => unwrap(api.post(`${BASE}/key-pairs/create/`, data)),

  // ── Policies ───────────────────────────────────────────────────────────

  /** List policies with optional query params (filters, pagination). */
  listPolicies: (params) => unwrap(api.get(`${BASE}/policies/`, { params })),

  /** Get a single policy by ID. */
  getPolicy: (id) => unwrap(api.get(`${BASE}/policies/${id}/`)),

  /** Create a new policy. */
  createPolicy: (data) => unwrap(api.post(`${BASE}/policies/`, data)),

  /** Update an existing policy. */
  updatePolicy: (id, data) => unwrap(api.put(`${BASE}/policies/${id}/`, data)),

  /** Delete a policy. */
  deletePolicy: (id) => unwrap(api.delete(`${BASE}/policies/${id}/`)),

  /**
   * Clone an existing v1 policy. Body is optional — every field from the
   * source can be overridden. Server hard-blocks the save with a structured
   * error_code = "DUPLICATE_CRITERIA" payload if the resulting policy would
   * have the same effective criteria as another policy in the org.
   * @param {string} policyUniqueId
   * @param {object} data - { name?, description?, category?, enforcement?,
   *   provider_scope?, rule_type?, rules?, resource_types?, integration_ids?,
   *   tag_filters?, project?, is_active?, priority? }
   */
  clonePolicy: (policyUniqueId, data) =>
    unwrap(api.post(`${BASE}/policies/${policyUniqueId}/clone/`, data || {})),

  // ── Policy Utilities ───────────────────────────────────────────────────

  /** Evaluate policies against a resource configuration. */
  evaluatePolicy: (data) => unwrap(api.post(`${BASE}/policies/evaluate/`, data)),

  /**
   * Estimate the on-demand monthly cost of a proposed configuration.
   * Body: ``{resource_type, provider, region, configuration}``.
   * Response: ``{monthly, currency, breakdown, caveats, supported, cache_hit}``.
   */
  estimateCost: (data) => unwrap(api.post(`${BASE}/cost-estimate/`, data)),

  /**
   * Push the resource's ``expires_at`` further into the future.
   * Body: ``{seconds: <int, optional>}`` — caps at the blueprint's
   * ttl_extension_seconds. Returns the new expires_at + remaining
   * extension count.
   */
  extendTtl: (id, data = {}) =>
    unwrap(api.post(`${BASE}/${id}/extend-ttl/`, data)),

  /** Get available policy templates. */
  getPolicyTemplates: () => unwrap(api.get(`${BASE}/policies/templates/`)),

  /** Seed policies from templates. */
  seedPolicies: (data) => unwrap(api.post(`${BASE}/policies/seed/`, data)),

  /** Get known instance families from DB for policy rule dropdowns. */
  getInstanceFamilies: () => unwrap(api.get(`${BASE}/policies/instance-families/`)),

  /**
   * Fetch the org's Roles that are eligible as approvers on an
   * approval-category policy: have the ``approve_cloud_resource``
   * permission and at least one member. Response: { roles: [{slug, name, member_count}, ...] }.
   */
  getApproverRoles: () => unwrap(api.get(`${BASE}/approver-roles/`)),

  // ── Lifecycle Stages ──────────────────────────────────────────────
  //
  // Org-scoped taxonomy of stages (Production / Pre-production / etc.)
  // referenced by Blueprint + CloudResource via a stable string key.
  // Backed by Organisation.meta_data['lifecycle_stages'] on the BE —
  // the FE just sees a flat list-of-dicts contract and never has to
  // know it's stored as JSON. Every endpoint requires the
  // ``manage_lifecycle_stages`` permission (admin role + bypass via
  // IsOrganisationAdminOrHasPermission).

  /** Get the org's full lifecycle-stage taxonomy. Pass
   *  ``include_deprecated=true`` to include archived stages too
   *  (useful for the management drawer; the catalog filter chip
   *  should NOT include them). Response: { results: [...], count }. */
  listLifecycleStages: (params = {}) =>
    unwrap(api.get(`${BASE}/lifecycle-stages/`, { params })),

  /** Add a new custom stage. Required body: { key, label, color }.
   *  Optional: protected (default false), position (default end). */
  createLifecycleStage: (data) =>
    unwrap(api.post(`${BASE}/lifecycle-stages/`, data)),

  /** Update a stage's label / color / protected / position. The
   *  stage's ``key`` itself is immutable (referenced by blueprints +
   *  resources) — rename the label instead. */
  updateLifecycleStage: (key, data) =>
    unwrap(api.patch(`${BASE}/lifecycle-stages/${key}/`, data)),

  /** Soft-hide a stage. Refuses if it would leave zero active stages.
   *  Existing blueprints / resources tagged with the stage keep
   *  rendering correctly; the stage is only hidden from new selections. */
  deprecateLifecycleStage: (key) =>
    unwrap(api.post(`${BASE}/lifecycle-stages/${key}/deprecate/`)),

  /** Reorder. Body: { keys: [stage_key_in_order, ...] }. Keys not
   *  present are appended at the end with their existing relative
   *  order preserved. */
  reorderLifecycleStages: (orderedKeys) =>
    unwrap(api.post(`${BASE}/lifecycle-stages/reorder/`, { keys: orderedKeys })),

  // ── Blueprints (Golden Path MVP) ──────────────────────────────────
  //
  // Catalog the dev sees, plus the platform-admin authoring surface.
  // Created resources reference the blueprint via the new
  // ``blueprint_unique_id`` body field on ``createResource``; backend
  // merges blueprint defaults with the dev's overrides automatically.

  /** Catalog query. Pass ``include_drafts=true`` for the author view. */
  listBlueprints: (params = {}) =>
    unwrap(api.get(`${BASE}/blueprints/`, { params })),

  /** Detail (full default_configuration + user_input_fields). */
  getBlueprint: (blueprintUniqueId) =>
    unwrap(api.get(`${BASE}/blueprints/${blueprintUniqueId}/`)),

  // ── Blueprint Versioning (Phase B+) ───────────────────────────────
  //
  // Every blueprint has v1...vN, with one ACTIVE row matching
  // ``Blueprint.current_version_number``. List returns lightweight
  // metadata (no snapshot bodies) for the history panel; detail
  // returns the full snapshot for diff/inspection. Restore creates a
  // new version with the chosen older version's content (mutates
  // Blueprint to match, archives the previous active).

  /** List all versions of a blueprint (newest first).
   *  Response: { blueprint_unique_id, blueprint_name, current_version_number, results: [...], count }. */
  listBlueprintVersions: (blueprintUniqueId) =>
    unwrap(api.get(`${BASE}/blueprints/${blueprintUniqueId}/versions/`)),

  /** Fetch one version's full snapshot (default_configuration etc.). */
  getBlueprintVersion: (blueprintUniqueId, versionNumber) =>
    unwrap(api.get(`${BASE}/blueprints/${blueprintUniqueId}/versions/${versionNumber}/`)),

  /** Restore a past version. Creates a new active version with the
   *  chosen older version's content. Optional ``changelog`` body. */
  restoreBlueprintVersion: (blueprintUniqueId, versionNumber, data = {}) =>
    unwrap(api.post(
      `${BASE}/blueprints/${blueprintUniqueId}/versions/${versionNumber}/restore/`,
      data,
    )),

  /** Create a blueprint from scratch (rare; usual path is save-as-blueprint). */
  createBlueprint: (data) =>
    unwrap(api.post(`${BASE}/blueprints/`, data)),

  /** Edit. Partial update — only supplied fields change. */
  updateBlueprint: (blueprintUniqueId, data) =>
    unwrap(api.put(`${BASE}/blueprints/${blueprintUniqueId}/`, data)),

  /** Soft delete. Existing resources keep their (now-null) FK. */
  deleteBlueprint: (blueprintUniqueId) =>
    unwrap(api.delete(`${BASE}/blueprints/${blueprintUniqueId}/`)),

  /** Promote draft → published (becomes visible in dev catalog). */
  publishBlueprint: (blueprintUniqueId) =>
    unwrap(api.post(`${BASE}/blueprints/${blueprintUniqueId}/publish/`)),

  /** Hide from catalog without breaking existing resources. */
  archiveBlueprint: (blueprintUniqueId) =>
    unwrap(api.post(`${BASE}/blueprints/${blueprintUniqueId}/archive/`)),

  /**
   * Validate a prospective create-from-blueprint submission BEFORE
   * the dev clicks Submit. Returns blocking_errors / warnings /
   * approval routing without mutating anything. Frontend should
   * debounce ~300ms when calling on every form change.
   *
   * Body shape: { cloud_account, region, user_inputs, user_tags?, project? }
   */
  dryRunBlueprint: (blueprintUniqueId, data) =>
    unwrap(api.post(`${BASE}/blueprints/${blueprintUniqueId}/dry-run/`, data)),

  /**
   * "Save as Blueprint" right-click action — turns an existing
   * working CloudResource into a reusable Blueprint.
   *
   * Body shape: { name, description?, category?, user_input_fields?,
   *               allowed_regions?, status? }
   */
  saveResourceAsBlueprint: (resourceUniqueId, data) =>
    unwrap(api.post(`${BASE}/${resourceUniqueId}/save-as-blueprint/`, data)),

  /**
   * Live-query AWS for orderable RDS / ElastiCache instance classes for
   * the given engine + engine_version + region + cloud account. Drives
   * the instance_class_picker in the Resource Create / Edit form.
   *
   * Body shape: { service, engine, engine_version, region, account_id }
   */
  listRdsInstanceClasses: (data) =>
    unwrap(api.post(`${BASE}/instance-families/`, data)),


  // ══════════════════════════════════════════════════════════════
  // OPA POLICY MANAGEMENT (v2)
  // ══════════════════════════════════════════════════════════════

  // ── Policies (Definition) V2 ───────────────────────────────────────────

  /**
   * List policies with filters.
   * @param {object} params - { visibility, category, is_active, search }
   */
  listPoliciesV2: (params) => unwrap(api.get(`${BASE}/policies-v2/`, { params })),

  /**
   * Get single policy by ID.
   */
  getPolicyV2: (id) => unwrap(api.get(`${BASE}/policies-v2/${id}/`)),

  /**
   * Create new policy (org-owned or platform).
   * @param {object} data - {name, rule_type, rules, category, enforcement, visibility, ...}
   */
  createPolicyV2: (data) => unwrap(api.post(`${BASE}/policies-v2/`, data)),

  /**
   * Update existing policy.
   */
  updatePolicyV2: (id, data) => unwrap(api.put(`${BASE}/policies-v2/${id}/`, data)),

  /**
   * Patch policy (partial update).
   */
  patchPolicyV2: (id, data) => unwrap(api.patch(`${BASE}/policies-v2/${id}/`, data)),

  /**
   * Delete policy (only if no active attachments).
   */
  deletePolicyV2: (id) => unwrap(api.delete(`${BASE}/policies-v2/${id}/`)),

  /**
   * Clone a v2 policy into a new org-owned draft.
   *
   * Body fields (all optional; unspecified fields inherit deep-copies from
   * the source):
   *   name, description, category, enforcement, provider_scope, rule_type,
   *   rules, meta_data, resource_types, is_active, attachments[]
   *
   * ``attachments`` is a list of inline {integration_id?, project_id?,
   * tag_filters?, priority?, enforcement_override?} objects created
   * atomically with the cloned policy.
   *
   * Hard-blocks with HTTP 400 + body
   *   { error_code: "DUPLICATE_CRITERIA", existing: { id, policy_unique_id, name }, ... }
   * if the resulting policy would have the same effective criteria as
   * another policy already visible to the caller's org. Callers should
   * detect ``error_code === "DUPLICATE_CRITERIA"`` and surface the
   * existing policy as a deep-link before letting the user retry with
   * different criteria.
   *
   * @param {number} id - source policy primary key
   * @param {object} data - clone overrides (see above)
   */
  clonePolicyV2: (id, data) =>
    unwrap(api.post(`${BASE}/policies-v2/${id}/clone/`, data || {})),

  // ── Policy Attachments ─────────────────────────────────────────

  /**
   * List policy attachments with filters.
   * @param {object} params - { organisation_id, integration_id, is_active }
   */
  listAttachments: (params) => unwrap(api.get(`${BASE}/policy-attachments-v2/`, { params })),

  /**
   * Get single attachment by ID.
   */
  getAttachment: (id) => unwrap(api.get(`${BASE}/policy-attachments-v2/${id}/`)),

  /**
   * Create policy attachment.
   * @param {object} data - {policy, organisation, integration, priority, enforcement_override, resource_type_override}
   */
  createAttachment: (data) => unwrap(api.post(`${BASE}/policy-attachments-v2/`, data)),

  /**
   * Update attachment.
   */
  updateAttachment: (id, data) => unwrap(api.put(`${BASE}/policy-attachments-v2/${id}/`, data)),

  /**
   * Detach policy (soft delete).
   */
  detachPolicy: (id) => unwrap(api.delete(`${BASE}/policy-attachments-v2/${id}/`)),

  /**
   * Bulk attach policy to multiple accounts.
   * @param {object} data - {policy_id, organisation_id, integration_ids[], enforcement_override, priority, resource_type_override}
   */
  bulkAttach: (data) => unwrap(api.post(`${BASE}/policy-attachments-v2/bulk/`, data)),

  // ── Policy Sync Status ─────────────────────────────────────────

  /**
   * Get sync status for all orgs or specific org.
   */
  listSyncStatus: (params) => unwrap(api.get(`${BASE}/policy-sync-status-v2/`, { params })),

  /**
   * Get sync status for specific org.
   */
  getSyncStatus: (orgId) => unwrap(api.get(`${BASE}/policy-sync-status-v2/${orgId}/`)),

  /**
   * Trigger manual sync for org (admin only).
   */
  triggerSync: (orgId) => unwrap(api.post(`${BASE}/policy-sync-status-v2/${orgId}/sync/`)),

  // ── Policy Evaluation Logs ─────────────────────────────────────

  /**
   * Get evaluation history for a resource.
   */
  getEvaluationLog: (resourceId) =>
    unwrap(api.get(`${BASE}/policies/evaluation-log/${resourceId}/`)),

  /**
   * Get evaluation logs for org (admin only).
   */
  listEvaluationLogs: (params) =>
    unwrap(api.get(`${BASE}/policies/evaluation-logs/`, { params })),
};

export default cloudResourcesApi;
